import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import { postRequest } from '../../../lib/api';
import { useContext, useState } from 'react';
import { AuthContext } from '../../../authContext/context';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import toast from 'react-hot-toast';

function generatePassword(length = 12) {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*?';
  const all = upper + lower + digits + special;

  let password = '';
  password += upper[Math.floor(Math.random() * upper.length)];
  password += lower[Math.floor(Math.random() * lower.length)];
  password += digits[Math.floor(Math.random() * digits.length)];
  password += special[Math.floor(Math.random() * special.length)];

  for (let i = 4; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }

  return password
    .split('')
    .sort(() => 0.5 - Math.random())
    .join('');
}

export default function CreatePartner() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState(generatePassword());
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productList, setProductList] = useState([]);

  React.useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await postRequest('private/GetProducts', {}, authCtx.token);
        if (response?.status === 200 && Array.isArray(response.data)) {
          setProductList(response.data);
        } else {
          toast.error('Failed to load products');
        }
      } catch (err) {
        toast.error('Error fetching products');
        console.error(err);
      }
    };

    fetchProducts();
  }, [authCtx.token]);

  const regeneratePassword = () => {
    const newPass = generatePassword();
    setTempPassword(newPass);
  };

  const toggleProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        product_id: selectedProducts,
        userType: { type: 3, isActive: true },
        password: tempPassword,
        isChangePassword: 0,
      };

      const response = await postRequest('private/partner-register', payload, authCtx.token);
      if (response?.status === 201) {
        reset();
        setSelectedProducts([]);
        setTempPassword(generatePassword());
        navigate('/partners');
      }
    } catch (err) {
      console.error('Partner creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Partners', path: '/partners' },
          { label: 'Add Partner', path: '' },
        ]}
      />
      <br></br>
      <div className="page-header">
        <h1 className="mt-0">Add New Partner</h1>
      </div>

      <div className="page-top-formview mb-3">
        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Partner Information */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="partnerName">
              <Form.Label>Partner Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Partner Name"
                {...register('name', { required: 'Partner name is required' })}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* MLM Settings */}
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Check
                type="checkbox"
                label="Uses MLM Branches / Pricing"
                {...register('uses_mlm_branches')}
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Check
                type="checkbox"
                label="Uses MLM Automation and Email Templates"
                {...register('uses_mlm_automation')}
              />
            </Form.Group>
          </Row>

          {/* Product Selection */}
          <Row className="mb-3">
            <Form.Group as={Col}>
              <Form.Label>Select Products</Form.Label>
              <div className="p-3 border rounded bg-light">
                {productList.length === 0 ? (
                  <small className="text-muted">No products available</small>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {productList.map((product) => {
                      const isSelected = selectedProducts.includes(product._id);
                      return (
                        <span
                          key={product._id}
                          className={`badge ${isSelected ? 'bg-primary' : 'bg-secondary'} cursor-pointer`}
                          style={{
                            cursor: 'pointer',
                            padding: '8px 12px',
                            fontSize: '14px'
                          }}
                          onClick={() => toggleProduct(product._id)}
                        >
                          {product.title}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </Form.Group>
          </Row>

          {/* User Information */}
          <h5 className="mt-4 mb-3">Default User Account</h5>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="firstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="First Name"
                {...register('forename', { required: 'First name is required' })}
                isInvalid={!!errors.forename}
              />
              <Form.Control.Feedback type="invalid">
                {errors.forename?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="lastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Last Name"
                {...register('surname', { required: 'Last name is required' })}
                isInvalid={!!errors.surname}
              />
              <Form.Control.Feedback type="invalid">
                {errors.surname?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email address',
                  },
                })}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="phone_number">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Phone Number"
                {...register('phone_number')}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="username">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Username"
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters required',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_-]+$/,
                    message: 'Only letters, numbers, underscore and hyphen allowed'
                  }
                })}
                isInvalid={!!errors.username}
              />
              <Form.Control.Feedback type="invalid">
                {errors.username?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* Temporary Password */}
          <div className="alert alert-info mb-3">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <strong>Temporary Password:</strong>{' '}
                <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                  {tempPassword}
                </code>
                <br />
                <small>The partner will be prompted to change this password on first login.</small>
              </div>
              <Button
                size="small"
                onClick={regeneratePassword}
                disabled={loading}
                variant="text"
                style={{ minWidth: 'auto', padding: '4px 8px' }}
              >
                🔄
              </Button>
            </div>
          </div>

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="inherit" type="button" onClick={() => navigate('/partners')}>
              Cancel
            </Button>
            <Button variant="contained" className='red-bg red-button text-capitalize' type="submit" disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
            </Button>
          </Stack>
        </Form>
      </div>
    </>
  );
}
